import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import FilterDropdown from "../../components/FilterDropdown";

import { NumericFilterEditor } from "./NumericFilterEditor";

const QUICK_VALUES = [1, 2, 3, 5] as const;

export function MinReferenceCountControl({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
}) {
  const isQuickValue =
    value !== null && (QUICK_VALUES as readonly number[]).includes(value);
  const selectedValue = value ?? 0;
  const [customValue, setCustomValue] = useState(
    value !== null && !isQuickValue ? String(value) : "",
  );
  const [customOpen, setCustomOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (value !== null && !isQuickValue) {
      setCustomValue(String(value));
    }
  }, [isQuickValue, value]);

  const options = useMemo(() => {
    const items = [
      { value: "0", label: "Keine Mindestanzahl" },
      ...QUICK_VALUES.map((quick) => ({
        value: String(quick),
        label:
          quick === 1
            ? "Mindestens 1 frühere Position"
            : `Mindestens ${quick} frühere Positionen`,
      })),
    ];
    if (value !== null && !isQuickValue) {
      items.push({
        value: String(value),
        label:
          value === 1
            ? "Mindestens 1 frühere Position"
            : `Mindestens ${value} frühere Positionen`,
      });
    }
    items.push({ value: "custom", label: "Eigene Mindestanzahl …" });
    return items;
  }, [isQuickValue, value]);

  const closeCustomEditor = useCallback((restoreFocus: boolean) => {
    setCustomOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() =>
        anchorRef.current?.querySelector("button")?.focus(),
      );
    }
  }, []);

  return (
    <div ref={anchorRef} className="relative">
      <FilterDropdown
        label="Vergleichsbasis"
        ariaLabel="Mindestanzahl früherer Vergleichspositionen filtern"
        options={options}
        value={String(selectedValue)}
        defaultValue="0"
        showValue
        formatActive={(option) =>
          option.value === "0"
            ? "keine Mindestanzahl"
            : option.label.replace("Mindestens", "mind.")
        }
        onChange={(next) => {
          if (next === "custom") {
            setCustomValue(value !== null && !isQuickValue ? String(value) : "");
            setCustomOpen(true);
            return;
          }
          const parsed = Number(next);
          onChange(parsed === 0 ? null : parsed);
        }}
      />
      <NumericFilterEditor
        open={customOpen}
        title="Eigene Mindestanzahl früherer Positionen"
        ariaLabel="Eigene Mindestanzahl früherer Vergleichspositionen festlegen"
        value={customValue}
        onValueChange={setCustomValue}
        onApply={(next) => {
          onChange(next);
          closeCustomEditor(true);
        }}
        onClose={closeCustomEditor}
        isValid={(next) => Number.isInteger(next) && next >= 1}
        invalidMessage="Bitte eine ganze Zahl ab 1 eingeben."
        min={1}
        step={1}
        placeholder="z. B. 4"
      />
    </div>
  );
}
